import { Index, type JSX, Show } from 'solid-js'

import * as Field from '@/components/ui/field'
import * as Select from '@/components/ui/select'
import { Text } from '@/components/ui/text'
import { createListCollection } from '@ark-ui/solid'
import { ChevronDownIcon } from 'lucide-solid'

interface FilterSelectProps<T extends string> {
  label: string
  items: readonly T[] // Supports Ark UI's lists
  selected: T[]
  onChange: (value: T[]) => void
  allSelectedText?: string
  formatTriggerText?: (text: string) => string
  itemTemplate?: (item: T) => JSX.Element
}

export function FilterSelect<T extends string>(props: FilterSelectProps<T>) {
  const collection = createListCollection({ items: props.items as T[] })

  // Check if everything is selected in the underlying data
  const isAllSelected = () => props.selected.length === props.items.length

  // The Magic Trick: Feed empty to UI if all selected to keep checkboxes clear
  const uiValue = () => (isAllSelected() ? [] : props.selected)

  const handleValueChange = (details: { value: string[] }) => {
    const newValue = details.value as T[]

    // If they unchecked the last item, or manually checked every single item
    if (newValue.length === 0 || newValue.length === props.items.length) {
      props.onChange([...props.items]) // Reset to All
    } else {
      props.onChange(newValue)
    }
  }

  return (
    <Field.Root>
      <Field.Label>{props.label}</Field.Label>
      <Select.Root
        positioning={{ sameWidth: true }}
        multiple
        value={uiValue()}
        collection={collection}
        onValueChange={handleValueChange}
      >
        <Select.Control>
          <Select.Trigger height="auto">
            {/* Fix: Keep ValueText always mounted and evaluate text INSIDE
              the JSX brackets so SolidJS continuously tracks the changes.
            */}
            <Select.ValueText
              asChild={(renderProps) => (
                <Text py={2} textTransform='capitalize'>
                  {isAllSelected()
                    ? props.allSelectedText ?? 'All Selected'
                    : props.formatTriggerText
                      ? props.formatTriggerText(renderProps().children?.toString().replaceAll('-', ' ').replaceAll('_', ' ') || '')
                      : renderProps().children?.toString().replaceAll('-', ' ').replaceAll('_', ' ')}
                </Text>
              )}
            />
            <Select.Indicator>
              <ChevronDownIcon />
            </Select.Indicator>
          </Select.Trigger>
        </Select.Control>
        <Select.Positioner>
          <Select.Content>
            <Select.ItemGroup>
              <Index each={collection.items}>
                {(item) => (
                  <Select.Item item={item()} textTransform='capitalize'>
                    <Show
                      when={props.itemTemplate}
                      fallback={<Select.ItemText>{item()?.toString().replaceAll('-', ' ').replaceAll('_', ' ')}</Select.ItemText>}
                    >
                      {/* Render the JSX element directly without casting it to a string */}
                      {props.itemTemplate!(item() as T)}
                    </Show>
                    <Select.ItemIndicator>✓</Select.ItemIndicator>
                  </Select.Item>
                )}
              </Index>
            </Select.ItemGroup>
          </Select.Content>
        </Select.Positioner>
        <Select.HiddenSelect />
      </Select.Root>
    </Field.Root>
  )
}
